/**
 * StaffApprovalModal Component - Modal for admin to finalize staff details during approval
 */
import React, { useState, useEffect } from 'react'
import { X, User, Mail, Briefcase, Crown, Users, CheckCircle } from 'lucide-react'
import { OrganizationStaff, NewOrganizationStaff } from '@nexus/types'

interface JoinRequestData {
  id: string
  firstName: string
  lastName: string
  preferredName?: string
  email: string
  jobTitle?: string
  message?: string
}

interface StaffApprovalModalProps {
  isOpen: boolean
  onClose: () => void
  onApprove: (staffData: NewOrganizationStaff) => void
  onReject: (reason?: string) => void
  joinRequest: JoinRequestData | null
  existingStaff: OrganizationStaff[]
  isLoading: boolean
}

export default function StaffApprovalModal({ 
  isOpen, 
  onClose, 
  onApprove, 
  onReject, 
  joinRequest, 
  existingStaff,
  isLoading 
}: StaffApprovalModalProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    email: '',
    jobTitle: '',
    lineManagerId: '',
    isHeadOfOrganization: false,
    isAdmin: false,
    status: 'active' as 'active' | 'inactive'
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showRejectConfirm, setShowRejectConfirm] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  // Pre-populate form when modal opens with join request data
  useEffect(() => {
    if (isOpen && joinRequest) {
      setFormData({
        firstName: joinRequest.firstName || '',
        lastName: joinRequest.lastName || '',
        preferredName: joinRequest.preferredName || '',
        email: joinRequest.email || '',
        jobTitle: joinRequest.jobTitle || '',
        lineManagerId: '',
        isHeadOfOrganization: false,
        isAdmin: false,
        status: 'active'
      })
      setErrors({})
      setShowRejectConfirm(false)
      setRejectReason('')
    }
  }, [isOpen, joinRequest])

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    const debugData = {
      formDataEmail: formData.email,
      existingStaff: existingStaff.map(s => ({ 
        email: s.email, 
        status: s.status,
        id: s.id 
      })),
      joinRequestEmail: joinRequest?.email
    };

    // Required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    } else {
      // Check for existing active staff with same email, but allow if it's for the same join request
      const conflictingStaff = existingStaff.find(s => 
        s.email.toLowerCase() === formData.email.toLowerCase() && 
        s.status === 'active'
      );
      
      if (conflictingStaff && conflictingStaff.email.toLowerCase() !== joinRequest?.email.toLowerCase()) {
        // Only block if it's a different user's email
        console.log('❌ EMAIL CONFLICT FOUND (different user):', conflictingStaff);
        newErrors.email = 'This email address is already in use by a different staff member'
      } else if (conflictingStaff) {
        // Same user - allow update
      } else {
      }
    }

    // Business rules validation
    const hasHeadOfOrg = existingStaff.some(s => s.isHeadOfOrganization)
    
    if (formData.isHeadOfOrganization) {
      if (hasHeadOfOrg) {
        newErrors.isHeadOfOrganization = 'There can only be one head of organization'
      }
      if (formData.lineManagerId) {
        newErrors.lineManagerId = 'Head of organisation cannot have a line manager'
      }
    } else {
      // Non-head staff need a line manager (unless they're the first person)
      if (existingStaff.length > 0 && !formData.lineManagerId) {
        newErrors.lineManagerId = 'Line manager is required (except for head of organisation)'
      }
    }

    // Line manager validation
    if (formData.lineManagerId) {
      const lineManager = existingStaff.find(s => s.id === formData.lineManagerId)
      if (!lineManager) {
        newErrors.lineManagerId = 'Selected line manager not found'
      } else if (lineManager.status === 'inactive') {
        newErrors.lineManagerId = 'Line manager must be active'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle approve
  const handleApprove = () => {
    if (!validateForm()) return

    const staffData: NewOrganizationStaff = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      preferredName: formData.preferredName.trim() || undefined,
      email: formData.email.trim().toLowerCase(),
      jobTitle: formData.jobTitle.trim() || undefined,
      lineManagerId: formData.lineManagerId || undefined,
      isHeadOfOrganization: formData.isHeadOfOrganization,
      isAdmin: formData.isAdmin,
      status: formData.status,
      organizationId: 'current', // Will be set by backend
      createdBy: 'current-user' // Will be set by backend
    }

    onApprove(staffData)
  }

  // Handle reject confirmation
  const handleRejectConfirm = () => {
    onReject(rejectReason.trim() || undefined)
    setShowRejectConfirm(false)
    setRejectReason('')
  }

  // Get available line managers (active staff members, excluding the new person)
  const availableLineManagers = existingStaff.filter(s => 
    s.status === 'active' && s.email !== formData.email
  )

  if (!isOpen || !joinRequest) return null

  if (showRejectConfirm) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-background rounded-lg shadow-lg w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 className="text-xl font-semibold text-destructive">Reject Join Request</h2>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to reject this request?
              </p>
            </div>
            <button
              onClick={() => setShowRejectConfirm(false)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            <div className="bg-muted p-4 rounded-lg">
              <p className="font-medium">{joinRequest.firstName} {joinRequest.lastName}</p>
              <p className="text-sm text-muted-foreground">{joinRequest.email}</p>
              {joinRequest.jobTitle && (
                <p className="text-sm text-muted-foreground">{joinRequest.jobTitle}</p>
              )}
            </div>

            <div>
              <label htmlFor="rejectReason" className="block text-sm font-medium mb-2">
                Reason for rejection (optional)
              </label>
              <textarea
                id="rejectReason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide a reason for rejection..."
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground resize-none"
                rows={3}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t bg-muted/30">
            <button
              onClick={() => setShowRejectConfirm(false)}
              className="px-4 py-2 border border-input rounded-md hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRejectConfirm}
              disabled={isLoading}
              className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Rejecting...' : 'Reject Request'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold">Approve New Team Member</h2>
            <p className="text-sm text-muted-foreground">
              Review and finalize the staff member details
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Join Request Info */}
          {joinRequest.message && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Message from applicant:</h4>
              <p className="text-blue-800">{joinRequest.message}</p>
            </div>
          )}

          <form className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="flex items-center gap-2 text-sm font-medium mb-1">
                  <User className="h-4 w-4" />
                  First Name <span className="text-destructive">*</span>
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground ${
                    errors.firstName ? 'border-destructive' : 'border-input'
                  }`}
                  placeholder="Enter first name"
                />
                {errors.firstName && <p className="text-sm text-destructive mt-1">{errors.firstName}</p>}
              </div>

              <div>
                <label htmlFor="lastName" className="flex items-center gap-2 text-sm font-medium mb-1">
                  <User className="h-4 w-4" />
                  Last Name <span className="text-destructive">*</span>
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground ${
                    errors.lastName ? 'border-destructive' : 'border-input'
                  }`}
                  placeholder="Enter last name"
                />
                {errors.lastName && <p className="text-sm text-destructive mt-1">{errors.lastName}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="preferredName" className="flex items-center gap-2 text-sm font-medium mb-1">
                <User className="h-4 w-4" />
                Preferred Name
              </label>
              <input
                id="preferredName"
                type="text"
                value={formData.preferredName}
                onChange={(e) => setFormData({ ...formData, preferredName: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                placeholder="What would they like to be called?"
              />
            </div>

            <div>
              <label htmlFor="email" className="flex items-center gap-2 text-sm font-medium mb-1">
                <Mail className="h-4 w-4" />
                Email Address <span className="text-destructive">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-muted ${
                  errors.email ? 'border-destructive' : 'border-input'
                }`}
                placeholder="Enter email address"
                disabled // Email from join request shouldn't be editable
              />
              {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="jobTitle" className="flex items-center gap-2 text-sm font-medium mb-1">
                <Briefcase className="h-4 w-4" />
                Job Title
              </label>
              <input
                id="jobTitle"
                type="text"
                value={formData.jobTitle}
                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                placeholder="e.g., Marketing Manager, Software Developer"
              />
            </div>

            {/* Line Manager */}
            <div>
              <label htmlFor="lineManagerId" className="flex items-center gap-2 text-sm font-medium mb-1">
                <Users className="h-4 w-4" />
                Line Manager {!formData.isHeadOfOrganization && <span className="text-destructive">*</span>}
              </label>
              <select
                id="lineManagerId"
                value={formData.lineManagerId}
                onChange={(e) => setFormData({ ...formData, lineManagerId: e.target.value })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground ${
                  errors.lineManagerId ? 'border-destructive' : 'border-input'
                }`}
                disabled={formData.isHeadOfOrganization}
              >
                <option value="">Select line manager...</option>
                {availableLineManagers.map(staff => (
                  <option key={staff.id} value={staff.id}>
                    {staff.preferredName || `${staff.firstName} ${staff.lastName}`} - {staff.jobTitle}
                  </option>
                ))}
              </select>
              {errors.lineManagerId && <p className="text-sm text-destructive mt-1">{errors.lineManagerId}</p>}
            </div>

            {/* Special Roles */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <input
                  id="isHeadOfOrganization"
                  type="checkbox"
                  checked={formData.isHeadOfOrganization}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    isHeadOfOrganization: e.target.checked,
                    lineManagerId: e.target.checked ? '' : formData.lineManagerId
                  })}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="isHeadOfOrganization" className="flex items-center gap-2 text-sm font-medium">
                  <Crown className="h-4 w-4 text-yellow-500" />
                  Head of Organisation
                </label>
              </div>
              {errors.isHeadOfOrganization && <p className="text-sm text-destructive">{errors.isHeadOfOrganization}</p>}

              <div className="flex items-center space-x-3">
                <input
                  id="isAdmin"
                  type="checkbox"
                  checked={formData.isAdmin}
                  onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="isAdmin" className="text-sm font-medium">
                  Organisation Admin (can manage staff and settings)
                </label>
              </div>
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium mb-1">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-muted/30">
          <button
            onClick={() => setShowRejectConfirm(true)}
            disabled={isLoading}
            className="px-4 py-2 border border-destructive text-destructive rounded-md hover:bg-destructive/10 transition-colors disabled:opacity-50"
          >
            Reject
          </button>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 border border-input rounded-md hover:bg-accent transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApprove}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <CheckCircle className="h-4 w-4" />
            {isLoading ? 'Approving...' : 'Approve & Add to Team'}
          </button>
        </div>
      </div>
    </div>
  )
}
